import * as React from "react";
import { MapPin } from "lucide-react";
import type { GeoSuggestion } from "@taskiq/shared";
import { Input } from "@/components/ui/Field";
import { cn } from "@/lib/cn";
import { useApi } from "@/lib/useApi";
import { useDebounced } from "@/lib/useDebounced";

/**
 * Slower than the list pages' 300ms on purpose: a search box queries our own
 * database, this queries somebody else's free geocoder, and an address is
 * typed in one go rather than refined a word at a time. 500ms fires once per
 * pause instead of once per syllable.
 */
const ADDRESS_DEBOUNCE_MS = 500;

/**
 * The appointment's address, as search-as-you-type over `GET /geo/search`.
 *
 * ── Free text first, coordinates when they are true ─────────────────────────
 *
 * The field is an input, not a picker: an address the geocoder has never heard
 * of ("pole 4, back of the depot") is still an address a dispatcher needs to
 * write down. Picking a suggestion stores the label AND its coordinates;
 * typing anything by hand afterwards clears the coordinates, because a lat/lng
 * describing text somebody has since edited is a claim the record cannot back.
 *
 * ── Anchored to the field, not a portal ─────────────────────────────────────
 *
 * The list is absolutely positioned off the field's own wrapper rather than
 * through `usePopover`: this field lives inside a dialog whose body scrolls,
 * and an anchored portal there needs dismiss-on-scroll rules this list does
 * not earn. Floating (never in-flow - an in-flow list shoves the form around
 * under the user's cursor), capped and scrollable past four rows, gone on
 * selection, Escape, an outside click or an empty box.
 *
 * ── It is a combobox, so it behaves like one ────────────────────────────────
 *
 * It already claimed `role="combobox"` and `aria-autocomplete="list"` and then
 * answered no key but Escape, which is worse than claiming nothing: a screen
 * reader announces a control the keyboard cannot drive. Up and Down move an
 * active option, Enter takes it, Escape closes, and `aria-activedescendant`
 * carries which one is active - the reason the highlight is state here rather
 * than a `:hover` rule.
 *
 * The pending and empty rows exist for the same reason. A 500ms debounce over
 * somebody else's geocoder means a second of nothing between the last keystroke
 * and the list, and a silent field during it reads as a field that does not
 * search.
 */

export interface AddressValue {
  address: string;
  lat: number | null;
  lng: number | null;
}

export function AddressField({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  placeholder?: string;
}) {
  // Suggestions only while the user is actually typing - not on mount over a
  // saved address, where the "suggestions" would be invitations to change it.
  const [typing, setTyping] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const term = useDebounced(value.address.trim(), ADDRESS_DEBOUNCE_MS);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const asking = typing && term.length >= 3;
  const search = useApi<{ suggestions: GeoSuggestion[] }>(
    asking ? `/geo/search?q=${encodeURIComponent(term)}` : null
  );
  const suggestions = (typing ? search.data?.suggestions : undefined) ?? [];

  // Still typing past the threshold and nothing back yet. Distinct from "no
  // matches", which is an answer.
  const pending = asking && search.data === undefined && !search.error;
  const open = asking && (pending || suggestions.length > 0 || search.data !== undefined);

  // The active option resets whenever the list changes underneath it -
  // otherwise Enter takes whatever now sits at the old index.
  React.useEffect(() => setActive(-1), [term]);

  // Keeps the active row in view when it is driven past the visible four.
  React.useEffect(() => {
    if (active < 0) return;
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  // A click anywhere outside the field closes the list. On the wrapper rather
  // than on blur: blur fires before the option's own click lands, and eating
  // the selection that way is the classic combobox bug.
  React.useEffect(() => {
    if (!typing) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current?.contains(event.target as Node)) return;
      setTyping(false);
    }
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [typing]);

  function pick(suggestion: GeoSuggestion) {
    setTyping(false);
    setActive(-1);
    onChange({ address: suggestion.label, lat: suggestion.lat, lng: suggestion.lng });
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape" && open) {
      // Stopped, because this field lives in a dialog that closes on Escape and
      // dismissing the list is what the key means while it is showing.
      event.stopPropagation();
      setTyping(false);
      return;
    }
    if (suggestions.length === 0) return;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      // Wraps, and an inactive list enters at the top going down and the
      // bottom going up.
      setActive((current) => {
        const next = current + step;
        if (next < 0) return suggestions.length - 1;
        if (next >= suggestions.length) return 0;
        return next;
      });
      return;
    }

    if (event.key === "Enter" && active >= 0) {
      // Only when an option is active. Enter on a hand-typed address has to
      // reach the form and submit it, which is the whole point of the field
      // accepting text a geocoder has never heard of.
      event.preventDefault();
      pick(suggestions[active]!);
    }
  }

  return (
    <div className="addr" ref={rootRef}>
      <Input
        id={id}
        value={value.address}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-suggestions`}
        aria-activedescendant={active >= 0 ? `${id}-suggestion-${active}` : undefined}
        aria-autocomplete="list"
        onChange={(e) => {
          setTyping(true);
          // Hand-typed text and stored coordinates must not disagree, so the
          // edit drops them until a suggestion is picked again.
          onChange({ address: e.target.value, lat: null, lng: null });
        }}
        onKeyDown={onKeyDown}
      />

      {open && (
        <ul
          className="addr__list"
          id={`${id}-suggestions`}
          ref={listRef}
          role="listbox"
          aria-label="Address suggestions"
          aria-busy={pending}
        >
          {pending && <li className="addr__note">Searching…</li>}

          {!pending && suggestions.length === 0 && (
            <li className="addr__note">
              Nothing found. Keep typing - what you write is saved either way.
            </li>
          )}

          {suggestions.map((suggestion, index) => {
            // Most geocoder labels are "14 Kea Street, Hilo, Hawaii County,
            // ...". The first segment is what distinguishes two results; the
            // rest is what confirms one, and it belongs in a quieter line
            // rather than pushing the useful half off the end of the row.
            const [head, ...tail] = suggestion.label.split(", ");
            return (
              <li key={`${suggestion.lat},${suggestion.lng}`}>
                <button
                  type="button"
                  id={`${id}-suggestion-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className={cn("addr__option", index === active && "addr__option--active")}
                  // `onMouseDown` preventing default keeps focus in the input,
                  // so the field does not blur-and-close before the click on it
                  // lands - the classic combobox bug.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(suggestion)}
                >
                  <MapPin aria-hidden />
                  <span className="addr__option-text">
                    <span className="addr__option-head">{head}</span>
                    {tail.length > 0 && (
                      <span className="addr__option-rest">{tail.join(", ")}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {value.lat !== null && value.lng !== null && (
        <p className="addr__hint mono tnum">
          {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
        </p>
      )}
    </div>
  );
}
